import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { EmojiProvider } from 'react-apple-emojis';
import emojiData from 'react-apple-emojis/src/data.json';
import './App.scss';
import FilterSection from './components/FilterSection';
import ResultsSection from './components/ResultsSection';
import EducationModal from './components/EducationModal';
import AgeModal from './components/AgeModal';
import PayModal from './components/PayModal';
import FocusAreaModal from './components/FocusAreaModal';
import FilterOptionsModal from './components/FilterOptionsModal';
import TimeModal from './components/TimeModal';
import kcLogo from './images/logo.png';
import logomark from './images/logomark-test.png';

const getPayTypes = item => {
  const rawPayType = item?.payType;
  const payTypes = Array.isArray(rawPayType)
    ? rawPayType
    : (rawPayType ? [rawPayType] : []);

  return payTypes
    .filter(type => typeof type === 'string')
    .map(type => type.trim().toLowerCase())
    .filter(Boolean);
};
const getPayDescription = item => (item.payDescription || '').trim().toLowerCase();

const matchesPayFilter = (item, selectedPayType) => {
  const selected = (selectedPayType || '').trim().toLowerCase();
  if (!selected) return true;

  const payTypes = getPayTypes(item);

  if (selected === 'stipend') {
    return payTypes.includes('stipend') || getPayDescription(item).includes('stipend');
  }
  if (selected === 'service hours') {
    return payTypes.includes('service hours') || getPayDescription(item).includes('service hours');
  }

  return payTypes.includes(selected);
};

const normalizeTime = value => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const matchesTimeFilter = (item, selectedTime) => {
  const selected = normalizeTime(selectedTime);
  if (!selected) return true;

  const itemTime = normalizeTime(item?.time);
  if (!itemTime) return false;

  if (itemTime === 'both') {
    return selected === 'both' || selected === 'full-time' || selected === 'part-time';
  }

  if (selected === 'both') {
    return itemTime === 'both' || itemTime === 'full-time' || itemTime === 'part-time';
  }

  return itemTime === selected;
};

const EMPTY_FILTERS = {
  ageGroup: '',
  payType: '',
  timeType: '',
  educationLevel: '',
  focusArea: '',
};


function App() {
  const [allData, setAllData] = useState([]);
  const [activeFilter, setActiveFilter] = useState('latest');
  const [openModal, setOpenModal] = useState(null);
  const [showFilterBack, setShowFilterBack] = useState(false);
  const [forceCloseFilterMenu, setForceCloseFilterMenu] = useState(false);
  const [hideLowListingsAlert, setHideLowListingsAlert] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const { ageGroup, payType, timeType, educationLevel, focusArea } = filters;

  // Disable main page scroll when any modal is open (including stacked/secondary)
  useEffect(() => {
    const anyModalOpen = !!openModal || !!showFilterBack;
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [openModal, showFilterBack]);


  // Closes all modals (used for filter menu)
  const closeAllModals = useCallback(() => {
    setOpenModal(null);
    setShowFilterBack(false);
    setForceCloseFilterMenu(false);
  }, []);

  // Closes only the secondary modal, leaves filter menu open if present
  const closeSecondaryModal = useCallback(() => {
    setOpenModal(null);
    // do not touch setShowFilterBack
  }, []);

  const applyExclusiveFilter = useCallback((key, value, nextActiveFilter) => {
    setFilters({
      ...EMPTY_FILTERS,
      [key]: value,
    });
    setActiveFilter(nextActiveFilter);
  }, []);

  const resetFilters = useCallback(() => {
    setActiveFilter('latest');
    setFilters({ ...EMPTY_FILTERS });
  }, []);

  const handleFilterChange = useCallback(filter => {
    setActiveFilter(filter);
    setFilters({ ...EMPTY_FILTERS });
    setOpenModal(null);
    setShowFilterBack(false);
  }, []);

  const handleAgeClick = useCallback((fromFilterMenu = false) => {
    setShowFilterBack(fromFilterMenu);
    setOpenModal('age');
  }, []);
  const handlePayClick = useCallback((fromFilterMenu = false) => {
    setShowFilterBack(fromFilterMenu);
    setOpenModal('pay');
  }, []);
  const handleEducationClick = useCallback((fromFilterMenu = false) => {
    setShowFilterBack(fromFilterMenu);
    setOpenModal('education');
  }, []);
  const handleTimeClick = useCallback((fromFilterMenu = false) => {
    setShowFilterBack(fromFilterMenu);
    setOpenModal('time');
  }, []);
  const handleFocusAreaClick = useCallback((fromFilterMenu = false) => {
    setShowFilterBack(fromFilterMenu);
    setOpenModal('focusArea');
  }, []);
  const handleFilterMenuClick = useCallback(() => {
    setShowFilterBack(false);
    setForceCloseFilterMenu(false);
    setOpenModal('filters');
  }, []);

  const handleAgeApply = useCallback(value => {
    applyExclusiveFilter('ageGroup', value, value ? 'age' : 'latest');
    if (showFilterBack) setForceCloseFilterMenu(true);
  }, [applyExclusiveFilter, showFilterBack]);

  const handlePayApply = useCallback(value => {
    applyExclusiveFilter('payType', value, value ? 'pay' : 'latest');
    if (showFilterBack) setForceCloseFilterMenu(true);
  }, [applyExclusiveFilter, showFilterBack]);

  const handleEducationApply = useCallback(level => {
    applyExclusiveFilter('educationLevel', level, level ? 'education' : 'latest');
    if (showFilterBack) setForceCloseFilterMenu(true);
  }, [applyExclusiveFilter, showFilterBack]);

  const handleTimeApply = useCallback(value => {
    applyExclusiveFilter('timeType', value, value ? 'time' : 'latest');
    if (showFilterBack) setForceCloseFilterMenu(true);
  }, [applyExclusiveFilter, showFilterBack]);

  const handleFocusAreaApply = useCallback(value => {
    applyExclusiveFilter('focusArea', value, value ? 'volunteer' : 'latest');
    if (showFilterBack) setForceCloseFilterMenu(true);
  }, [applyExclusiveFilter, showFilterBack]);

  const showFilterMenu = openModal === 'filters' || showFilterBack;

  useEffect(() => {
    loadData();
  }, []);

  const filteredData = useMemo(() => {
    let filtered = [...allData];
    // Exclude closed items
    filtered = filtered.filter(item => !item.closed);
    if (activeFilter === 'pay') {
      filtered = filtered.filter(item => matchesPayFilter(item, payType));
    } else if (activeFilter === 'time') {
      filtered = filtered.filter(item => matchesTimeFilter(item, timeType));
    } else if (activeFilter === 'age') {
      if (ageGroup) {
        const ageGroupLower = ageGroup.toLowerCase();
        filtered = filtered.filter(item => {
          if (Array.isArray(item.ageRange)) {
            return item.ageRange.some(age => typeof age === 'string' && age.toLowerCase() === ageGroupLower);
          } else if (typeof item.ageRange === 'string') {
            return item.ageRange.toLowerCase() === ageGroupLower;
          }
          return false;
        });
      }
    } else if (activeFilter === 'education') {
      filtered = filtered.filter(item => {
        const levels = Array.isArray(item.academicLevel)
          ? item.academicLevel
          : (item.academicLevel ? [item.academicLevel] : []);

        if (!educationLevel) return levels.length > 0;

        const educationLevelLower = educationLevel.toLowerCase();
        return levels.some(level => typeof level === 'string' && level.toLowerCase() === educationLevelLower);
      });
    } else if (activeFilter === 'volunteer') {
      filtered = filtered.filter(item =>
        (item.focusAreas || []).some(a =>
          typeof a === 'string' && typeof focusArea === 'string'
            ? a.toLowerCase() === focusArea.toLowerCase()
            : a === focusArea
        )
      );
    }
    filtered.sort((a, b) => {
      const aId = Number(a?.id);
      const bId = Number(b?.id);
      const safeA = Number.isFinite(aId) ? aId : -Infinity;
      const safeB = Number.isFinite(bId) ? bId : -Infinity;
      return safeB - safeA;
    });

    return filtered;
  }, [activeFilter, ageGroup, payType, timeType, educationLevel, focusArea, allData]);

  const openListingsCount = useMemo(() => {
    return allData.filter(item => {
      if (item?.closed === false) return true;
      if (typeof item?.closed === 'string') {
        return item.closed.trim().toLowerCase() === 'false';
      }
      return false;
    }).length;
  }, [allData]);

  useEffect(() => {
    if (openListingsCount > 3) {
      setHideLowListingsAlert(false);
    }
  }, [openListingsCount]);

  const loadData = async () => {
    try {
      const response = await fetch(`${process.env.PUBLIC_URL || ''}/data.json`);
      if (response.ok) {
        const data = await response.json();
        setAllData(data);
      } else {
        setAllData([]);
      }
    } catch (error) {
      setAllData([]);
    }
  };

  return (
    <EmojiProvider data={emojiData}>
      <div id="page-top" />
      <a href="#main-content" className="skip-to-main">Skip to main content</a>
      <div className="kc-logo-bar container">
        <img src={kcLogo} alt="King County" className="kc-logo-img" />
      </div>
      <header>
        <h1>YOUTHWORKS</h1>
      </header>
      <main id="main-content" className="container">
        <FilterSection
          activeFilter={activeFilter}
          onFilterChange={handleFilterChange}
          onAgeClick={handleAgeClick}
          onPayClick={handlePayClick}
          onTimeClick={handleTimeClick}
          onEducationClick={handleEducationClick}
          onFocusAreaClick={handleFocusAreaClick}
          onFilterMenuClick={handleFilterMenuClick}
        />
        {openListingsCount <= 3 && !hideLowListingsAlert && (
          <div className="low-listings-alert" role="status" aria-live="polite">
            <div className="low-listings-alert-content">
              <div className="low-listings-alert-copy">
                <h2 className="low-listings-alert-heading">Not many listings right now?</h2>
                <p className="low-listings-alert-text">No worries! We drop new stuff every season 🍂❄️🌸☀️</p>
                <p className="low-listings-alert-text">Come back soon to see what’s fresh!</p>
              </div>
              <button
                type="button"
                className="low-listings-alert-close"
                onClick={() => setHideLowListingsAlert(true)}
                aria-label="Dismiss low listings alert"
              >
                ×
              </button>
            </div>
          </div>
        )}
        {showFilterMenu && (
          <FilterOptionsModal
            onClose={closeAllModals}
            forceClose={forceCloseFilterMenu}
            onEducationClick={() => handleEducationClick(true)}
            onAgeClick={() => handleAgeClick(true)}
            onPayClick={() => handlePayClick(true)}
            onTimeClick={() => handleTimeClick(true)}
            onFocusAreaClick={() => handleFocusAreaClick(true)}
          />
        )}
        {openModal === 'age' && (
          <AgeModal
            data={allData}
            currentValue={ageGroup}
            onApply={handleAgeApply}
            onClose={closeSecondaryModal}
            onResetAll={resetFilters}
            onBack={showFilterBack ? handleFilterMenuClick : undefined}
          />
        )}
        {openModal === 'pay' && (
          <PayModal
            data={allData}
            currentValue={payType}
            onApply={handlePayApply}
            onClose={closeSecondaryModal}
            onResetAll={resetFilters}
            onBack={showFilterBack ? handleFilterMenuClick : undefined}
          />
        )}
        {openModal === 'education' && (
          <EducationModal
            data={allData}
            currentValue={educationLevel}
            onApply={handleEducationApply}
            onClose={closeSecondaryModal}
            onResetAll={resetFilters}
            onBack={showFilterBack ? handleFilterMenuClick : undefined}
          />
        )}
        {openModal === 'time' && (
          <TimeModal
            data={allData}
            currentValue={timeType}
            onApply={handleTimeApply}
            onClose={closeSecondaryModal}
            onResetAll={resetFilters}
            onBack={showFilterBack ? handleFilterMenuClick : undefined}
          />
        )}
        {openModal === 'focusArea' && (
          <FocusAreaModal
            data={allData}
            currentValue={focusArea}
            onApply={handleFocusAreaApply}
            onClose={closeSecondaryModal}
            onResetAll={resetFilters}
            onBack={showFilterBack ? handleFilterMenuClick : undefined}
          />
        )}
        <ResultsSection
          data={filteredData}
          activeFilter={activeFilter}
          ageGroup={ageGroup}
          payType={payType}
          timeType={timeType}
          educationLevel={educationLevel}
          focusArea={focusArea}
          onResetFilter={resetFilters}
        />
      </main>
      <footer className="site-footer" id="site-footer">
        <div className="footer-inner">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="footer-logo-mark" aria-hidden="true">
                <img src={logomark} alt="" className="footer-logo-image" />
              </div>
              <span className="footer-wordmark" aria-hidden="true">YOUTHWORKS</span>
            </div>
            <nav className="footer-nav" aria-label="Footer navigation">
              <a href="mailto:execyouthworks@kingcounty.gov">CONTACT US</a>
              <a href="https://kingcounty.gov/en/shared-topics/about-king-county/website/terms-of-use">TERMS OF USE</a>
              <a href="https://kingcounty.gov/en/shared-topics/about-king-county/website/privacy">PRIVACY</a>
            </nav>
          </div>
          <div className="footer-copyright">© King County, WA 2026.</div>
        </div>
      </footer>
    </EmojiProvider>
  );
}

export default App;
